// src/modules/app-views/services/pages/Init/modals/ServicesParametersModal/tabs/CategoriesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Tree, Button, Flex, Space, Popconfirm, Typography } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import CategoryFormModal from './CategoryFormModal';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CategoriesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [parentNode, setParentNode] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for categories - initialize from store
  const [categories, setCategories] = useState(() => {
    const rawCategories = appService.getModulePreferences({ category: 'categories' });
    return normalizeToArray(rawCategories);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAddRoot = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setParentNode(null);
    setShowModal(true);
  }, []);

  const handleAddChild = useCallback((node, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setParentNode(node);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((node, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(node);
    setParentNode(null);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      const { label, parentUid } = formData;

      // Check for duplicates at the same level
      const siblings = parentUid
        ? findNodeByKey(categories, parentUid)?.children || []
        : categories;

      const isDuplicate = siblings.some(
        c =>
          c.uid !== editingRecord?.uid &&
          c.label.toLowerCase() === label.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedCategories;

        if (editingRecord) {
          // Editing existing category
          updatedCategories = updateNodeInTree(categories, editingRecord.uid, { label });
        } else if (parentUid) {
          // Adding subcategory
          const newCategory = {
            uid: generateUidV4(),
            label,
            children: [],
          };
          updatedCategories = addChildToTree(categories, parentUid, newCategory);

          // Expand parent to show new child
          if (!expandedKeys.includes(parentUid)) {
            setExpandedKeys([...expandedKeys, parentUid]);
          }
        } else {
          // Adding root category
          const newCategory = {
            uid: generateUidV4(),
            label,
            children: [],
          };
          updatedCategories = [...categories, newCategory];
        }

        await appService.updateModulePreferences({
          category: 'categories',
          data: updatedCategories,
        });

        setCategories(updatedCategories);
        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
        setParentNode(null);
      } catch (error) {
        console.error('[CategoriesTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [categories, editingRecord, expandedKeys, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    setParentNode(null);
  }, []);

  const handleDelete = useCallback(
    async nodeId => {
      setLoading(true);
      try {
        const updatedCategories = removeNodeFromTree(categories, nodeId);

        await appService.updateModulePreferences({
          category: 'categories',
          data: updatedCategories,
        });

        setCategories(updatedCategories);
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch (error) {
        console.error('[CategoriesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [categories, setLoading]
  );

  const handleExpand = useCallback(keys => {
    setExpandedKeys(keys);
  }, []);

  const handleExpandAll = useCallback(() => {
    const allKeys = getAllNodeKeys(categories);
    setExpandedKeys(allKeys);
  }, [categories]);

  const handleCollapseAll = useCallback(() => {
    setExpandedKeys([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. TREE DATA
  // ─────────────────────────────────────────────────────────────────────────────
  const treeData = useMemo(
    () => buildTreeData(categories, { handleEdit, handleDelete, handleAddChild, loading }),
    [categories, handleEdit, handleDelete, handleAddChild, loading]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical>
      <div style={{ maxHeight: 'calc(80vh - 240px)', overflow: 'auto' }}>
        {categories.length === 0 ? (
          <Flex justify="center" align="center" style={{ minHeight: 120 }}>
            <Text type="secondary">{translateX('no_data')}</Text>
          </Flex>
        ) : (
          <Tree
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={handleExpand}
            showLine={{ showLeafIcon: false }}
            showIcon
            icon={props => (props.expanded ? <FolderOpenOutlined /> : <FolderOutlined />)}
            selectable={false}
            blockNode
            className="categories-tree"
          />
        )}
      </div>

      <Flex justify="space-between" align="center" className="mt-3" style={{ flexShrink: 0 }}>
        <Space size="small">
          <Tooltip title={translateX('expand_all')}>
            <Button
              type="text"
              icon={<DownOutlined />}
              onClick={handleExpandAll}
              disabled={loading || categories.length === 0}
            />
          </Tooltip>
          <Tooltip title={translateX('collapse_all')}>
            <Button
              type="text"
              icon={<UpOutlined />}
              onClick={handleCollapseAll}
              disabled={loading || categories.length === 0}
            />
          </Tooltip>
        </Space>

        <Button icon={<PlusOutlined />} onClick={handleAddRoot} disabled={loading}>
          {translateX('add_category')}
        </Button>
      </Flex>

      <CategoryFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        parentNode={parentNode}
        loading={loading}
        mousePosition={mousePosition}
      />

      <style>{`
        .categories-tree .ant-tree-treenode {
          align-items: center;
          padding: 4px 0;
        }
        .categories-tree .ant-tree-node-content-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
        }
        .categories-tree .ant-tree-title {
          flex: 1;
          min-width: 0;
        }
        .categories-tree .ant-tree-iconEle {
          flex-shrink: 0;
        }
      `}</style>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CategoriesTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTreeData(nodes, handlers) {
  const { handleEdit, handleDelete, handleAddChild, loading } = handlers;
  const arr = normalizeToArray(nodes);

  return arr.map(node => {
    const nodeChildren = normalizeToArray(node.children);
    const hasChildren = nodeChildren.length > 0;

    const title = (
      <Flex align="center" justify="space-between" style={{ width: '100%', minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.label}
        </span>
        <Space size={0} style={{ flexShrink: 0, marginLeft: 8 }}>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleAddChild(node, e);
            }}
            disabled={loading}
            title={translateX('add_subcategory')}
          />
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleEdit(node, e);
            }}
            disabled={loading}
          />
          <Popconfirm
            title={
              hasChildren
                ? translateX('confirm_delete_with_children')
                : translateX('confirm_delete')
            }
            onConfirm={() => handleDelete(node.uid)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={e => e.stopPropagation()}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      </Flex>
    );

    const children = hasChildren ? buildTreeData(nodeChildren, handlers) : undefined;

    return {
      key: node.uid,
      title,
      children,
      isLeaf: !hasChildren,
    };
  });
}

function findNodeByKey(nodes, nodeId) {
  const arr = normalizeToArray(nodes);
  for (const node of arr) {
    if (node.uid === nodeId) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, nodeId);
      if (found) return found;
    }
  }
  return null;
}

function addChildToTree(nodes, parentId, newChild) {
  const arr = normalizeToArray(nodes);
  return arr.map(node => {
    if (node.uid === parentId) {
      return {
        ...node,
        children: [...normalizeToArray(node.children), newChild],
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: addChildToTree(node.children, parentId, newChild),
      };
    }
    return node;
  });
}

function updateNodeInTree(nodes, nodeId, updates) {
  const arr = normalizeToArray(nodes);
  return arr.map(node => {
    if (node.uid === nodeId) {
      return { ...node, ...updates };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(node.children, nodeId, updates),
      };
    }
    return node;
  });
}

function removeNodeFromTree(nodes, nodeId) {
  const arr = normalizeToArray(nodes);
  return arr
    .filter(node => node.uid !== nodeId)
    .map(node => {
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: removeNodeFromTree(node.children, nodeId),
        };
      }
      return node;
    });
}

function normalizeToArray(data) {
  if (Array.isArray(data)) return data;
  if (data == null) return [];

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    const isNumericKeys = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
    if (isNumericKeys) {
      return keys.sort((a, b) => Number(a) - Number(b)).map(k => {
        const item = data[k];
        if (item && item.children) {
          return { ...item, children: normalizeToArray(item.children) };
        }
        return item;
      });
    }
  }

  return [];
}

function getAllNodeKeys(nodes) {
  const arr = normalizeToArray(nodes);
  let keys = [];
  for (const node of arr) {
    keys.push(node.uid);
    if (node.children && node.children.length > 0) {
      keys = keys.concat(getAllNodeKeys(node.children));
    }
  }
  return keys;
}
